import React, { useEffect, useRef, useState } from 'react'
import Honey, { honeyGenerator } from '../fragments/honey'
import { blindPostRequest } from '../../utils/network'
import { getPropValues } from '../../utils/helpers'
import PostHogHelper from '../../utils/posthog-helper'
import { ContactType } from './campaign'
import Consent from '../fragments/consent'
import FormError from '../fragments/formError'
import { navigate } from 'gatsby'

/**
 * This should serve as form builder for all forms.
 */
const FormBuilder: React.FC<{
  form: {
    type: 'newsletter' | 'contact' | 'subscription'
    action?: string
    redirect?: string
    terms?: string
  }
  fields: {
    type: string
    placeholder?: string
    label?: string
    text?: string
  }[]
}> = ({ form, fields }) => {
  const [formId, setFormId] = useState('')
  const formRef = useRef<HTMLFormElement>(null)
  const validRef = useRef<HTMLInputElement>(null)
  const consentRef = useRef<HTMLInputElement>(null)
  const mailRef = useRef<HTMLInputElement>(null)

  const [submitted, setSubmitted] = useState(false)
  const [success, setSuccess] = useState(true)

  const actionUrl = getPropValues(
    form?.action,
    'https://api.bunny.net/newsletter/add-contact'
  )
  const redirectUrl = getPropValues(
    form?.redirect,
    '/thank-you/'
  )
  const termsText = getPropValues(
    form?.terms,
    'I have read and agree to the bunny.net <a href="/privacy">Privacy Policy</a>.'
  )

  const handleSubmit = async (event) => {
    event.preventDefault()

    setSubmitted(true)

    const formJson = {
      ContactEmail: mailRef.current?.value,
      Email: mailRef.current?.value,
      IsValid: `${validRef.current?.checked}`,
      Type: ContactType.Sales,
      AgreeWithTerms: consentRef.current?.checked,
      Message: `I would like to join bunny.net's newsletter to stay in touch with the new case studies.`,
    }

    const postedSuccessfully = await blindPostRequest(actionUrl, formJson)

    if (postedSuccessfully) {
      await PostHogHelper.promiseToCaptureEmail(formJson.ContactEmail)
      navigate(redirectUrl)
    } else {
      setSuccess(false)
    }
  }

  useEffect(() => {
    if (!formId) {
      setFormId(honeyGenerator())
    }
  })

  return (
    <form
      ref={formRef}
      className={`bunny-form bunny-form--${form.type} ${
        success ? 'visibility-hidden' : ''
      }`}
      onSubmit={handleSubmit}
    >
      <Honey validRef={validRef} formId={formId} />
      {fields.map((field) => {
        switch (field.type) {
          case 'email':
            return (
              <FormMail
                key={field.type}
                formId={formId}
                mailRef={mailRef}
                placeholder={field.placeholder}
              />
            )
          case 'submit':
            return <SubmitButton key={field.type} text={field.text} />
          default:
            return null
        }
      })}
      <Consent consentRef={consentRef} formId={formId} text={termsText} />
      <FormError submitted={submitted} success={success} />
    </form>
  )
}

const FormMail: React.FC<{ formId: string; placeholder: string, mailRef: React.RefObject<HTMLInputElement> }> = ({
  formId,
  placeholder,
  mailRef
}) => {
  const emailId = `ContactEmail${formId}`

  const [email, setEmail] = useState('')

  const handleChange = (event) => {
    const target = event.target
    setEmail(target.value)
  }

  return (
    <input
      ref={mailRef}
      type="email"
      id={emailId}
      name={emailId}
      className="bunny-form__field bunny-form__field--email"
      placeholder={placeholder}
      value={email}
      onChange={handleChange}
      required
    />
  )
}

const SubmitButton: React.FC<{ text: string }> = ({ text }) => (
  <button
    type="submit"
    name="submit"
    className="cta cta-primary cursor-pointer bunny-form__cta"
  >
    {text}
  </button>
)

export default FormBuilder
